<script lang="ts">
    import { get } from 'svelte/store';
    import { api } from '../../../stores/api';
    import { app } from '../../../stores/app';
    import { onMount } from 'svelte';
    import { z } from 'zod';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import Select from '../../shared/Select.svelte';
    import { debug } from '../../../stores/store';
    import {_} from 'svelte-i18n';

    // Available customer ids with names fetched from backend
    let availableOptions: { id: number; name: string }[] = [];
    let selectedCustomerId: number | null = null;
    let selectedCustomerIdStr: string = '';
    let loading = false;
    let saving = false;
    let error = '';

    // Determine if master_customer is enabled for this user
    // $: isMasterCustomerEnabled = $app.account?.getMasterCustomerId() !== null && $app.account?.getCustomerId() !== $app.account?.getMasterCustomerId();
    $: isMasterCustomerEnabled = $app.account?.getSettingsReadonly()?.master_customer.enabled === true || ($app.account?.getMasterCustomerId() !== null && $app.account?.getCustomerId() !== $app.account?.getMasterCustomerId());

    const selectedMasterAccount = $app.account?.getMasterCustomerId() !== null && $app.account?.getCustomerId() === $app.account?.getMasterCustomerId();

    onMount(async () => {
        if (isMasterCustomerEnabled || selectedMasterAccount) {
            selectedCustomerId = $app.account?.getCustomerId() ?? null;
            selectedCustomerIdStr = selectedCustomerId !== null ? '#' + String(selectedCustomerId) + ' - ' + $app.account?.getName() : '';
            console.log('selectedCustomerId', selectedCustomerId, selectedCustomerIdStr);
            await fetchAvailableCustomerIds();
        } else {
            console.info('### SelectCustomerId: master customer feature is not enabled for this user, skipping loading available customer ids', isMasterCustomerEnabled, selectedMasterAccount);
        }
    });

    async function fetchAvailableCustomerIds() {
        loading = true;
        error = '';
        try {
            const result = await get(api).getData<any, { id: number; name: string }[]>(
                'post',
                '/user/get_available_customer_ids',
                {},
                z.any(),
                z.array(z.object({ id: z.number(), name: z.string() })),
            );
            availableOptions = result ?? [];
            console.log('### availableOptions', availableOptions);

            if (selectedCustomerId !== null && !availableOptions.some(opt => opt.id === selectedCustomerId)) {
                console.warn('Selected customer id is not in the list of available options, resetting to null', availableOptions);
                // set first available from list
                await selectCustomerId(availableOptions.length > 0 ? availableOptions[0].id : null, false);
            }

        } catch (e) {
            console.error('SelectCustomerId: failed to load available customer ids', e);
            error = $_('load_list_error');
        } finally {
            loading = false;
        }
    }

    async function selectCustomerId(customerId: number | null, reload: boolean) {
        saving = true;
        error = '';
        try {
            await get(api).getData<any, any>(
                'post',
                '/user/set_slave_customer_id',
                { customer_id: customerId },
                z.object({ customer_id: z.number().nullable() }),
                z.any(),
            );

            if (reload) {
                // https://er.automapa.pl/issues/50519
                // Redirect to a safe URL (without resource IDs) to avoid conflicts between customers.
                // If the current path contains numeric segments (e.g. /edit-job/123), go to root.
                // Otherwise reload the current section so context stays consistent.
                const pathSegments = window.location.pathname.split('/');
                const cleanedSegments = pathSegments.filter(seg => !/^\d+$/.test(seg));
                const safePath = cleanedSegments.join('/') || '/';
                window.location.href = window.location.origin + safePath;
            } else {
                alert($_('account_selected_reload', { values: { accountId: customerId !== null ? '#' + customerId : $_('own_account') } }));
            }
        } catch (e) {
            console.error('SelectCustomerId: failed to set slave customer id', e);
            error = $_('save_error');
            saving = false;
        }
    }

    $: selectedCustomerIdStr = selectedCustomerIdStr;
</script>

{#if availableOptions.length > 0}
    <div class="select-customer-id-wrapper">
        {#if loading}
            <Spinner />
        {:else}
            {#if $debug}
                <div class="select-customer-id-label">{$_('Depot')}:</div>
                <select
                    class="select-customer-id-select"
                    disabled={saving}
                    bind:value={selectedCustomerId}
                    on:change={() => selectCustomerId(selectedCustomerId, true)}
                >
                    {#if selectedMasterAccount}
                        <option value={$app.account?.getMasterCustomerId()}>- {$_('own_account')} -</option>
                    {:else}
                        <option value={$app.account?.getCustomerId() ?? null}>{selectedCustomerIdStr}</option>
                    {/if}
                    {#each availableOptions as opt (opt.id)}
                        <option value={opt.id}>#{opt.id} - {opt.name}</option>
                    {/each}
                </select>

                {#if selectedCustomerId !== null}
                    <button
                        class="select-customer-id-clear"
                        disabled={saving}
                        title={$_('return_to_own_account')}
                        on:click={() => selectCustomerId(null, true)}
                    >
                        ✕
                    </button>
                {/if}
            {:else}
                <div class="select-customer-id-label">{$_('Depot')}:</div>
                <Select
                    items={availableOptions.map(opt => ({ id: opt.id.toString(), label: `${opt.name}` }))}
                    selected={selectedCustomerId?.toString() ?? ''}
                    disabled={saving}
                    onChange={(selected) => {
                        selectCustomerId(parseInt(selected), true);
                        return true;
                    }}
                />
            {/if}

            {#if saving}
                <Spinner />
            {/if}

            {#if error}
                <span class="select-customer-id-error">{error}</span>
            {/if}
        {/if}
    </div>
{/if}

<style>
    .select-customer-id-wrapper {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 13px;
    }

    .select-customer-id-label {
        font-weight: 600;
        color: #7a5c00;
        white-space: nowrap;
    }

    .select-customer-id-select {
        font-size: 13px;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 2px 6px;
        background: #fff;
        cursor: pointer;
        min-width: 150px;
    }

    .select-customer-id-select:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .select-customer-id-clear {
        background: none;
        border: 1px solid #aaa;
        border-radius: 4px;
        padding: 2px 7px;
        cursor: pointer;
        font-size: 12px;
        color: #555;
        line-height: 1;
    }

    .select-customer-id-clear:hover:not(:disabled) {
        background: #f0f0f0;
    }

    .select-customer-id-clear:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .select-customer-id-error {
        color: #c00;
        font-size: 12px;
    }
</style>




